import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import CalendarioAgenda from "../components/CalendarioAgenda";
import CalendarioDisponibilidade from "./CalendarioDisponibilidade";
import ChatWhatsapp from "./ChatWhatsapp";
import BotaoContratacao from "./BotaoContratacao.jsx";
import { Clock, ShieldCheck, Star } from "lucide-react";
import { getBabaPhotoCandidates, handleBabaPhotoError, BABA_PLACEHOLDER } from "../utils/babaPhoto.js";

const PLACEHOLDER_BABA_URL = BABA_PLACEHOLDER;

const calculateAge = (dob) => {
  if (!dob) return null;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  return age;
};

const parseArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === "string" && val.startsWith("[")) {
    try { return JSON.parse(val); } catch (e) { return []; }
  }
  return typeof val === "string" && val.trim() ? [val] : [];
};

const parseObject = (val) => {
  if (!val) return {};
  if (typeof val === "object" && !Array.isArray(val)) return val;
  if (typeof val === "string") {
    try { return JSON.parse(val); } catch (e) { return {}; }
  }
  return {};
};

const sanitizePublicBabaProfile = (perfil) => ({
  id: perfil.id,
  nome: perfil.nome,
  sobrenome: perfil.sobrenome,
  foto_url: perfil.foto_url,
  cidade: perfil.cidade,
  nivel_experiencia: perfil.nivel_experiencia,
  experiencia_text: perfil.experiencia_text,
  bio: perfil.bio,
  area_atuacao: parseArray(perfil.area_atuacao),
  disponibilidade: parseObject(perfil.disponibilidade),
  idiomas_falados: parseArray(perfil.idiomas_falados),
  servicos_extras: parseArray(perfil.servicos_extras),
  aceita_emergencial: perfil.aceita_emergencial,
  raio_atendimento_km: perfil.raio_atendimento_km,
  avgRating: perfil.total_avaliacoes > 0 ? perfil.avaliacao_media : 0,
  countReviews: perfil.total_avaliacoes || 0,
  certificados_docs: perfil.certificados_docs,
  idade: calculateAge(perfil.data_nascimento),
});

const getPathFromPublicUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  if (!url.startsWith("http")) return url;
  const match = url.match(/\/storage\/v1\/object\/(?:public|sign)\/[^\/]+\/(.*)/);
  return match ? match[1].split("?")[0] : "";
};

const sectionCls = "bg-white rounded-2xl border border-[var(--color-warm-100)] shadow-sm p-5 mb-4";
const h2Cls = "text-base font-bold text-[var(--color-slate-blue-400)] mb-3";
const tagCls = "inline-block rounded-xl bg-[var(--color-rose-400)]/10 text-[var(--color-rose-400)] px-3 py-1 text-xs font-semibold";

const StarRow = ({ nota = 0, size = 14 }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star
        key={s}
        size={size}
        className={s <= Math.round(nota) ? "fill-amber-400 text-amber-400" : "fill-warm-200 text-warm-200"}
      />
    ))}
  </span>
);

const formatMesAno = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
    return d.charAt(0).toUpperCase() + d.slice(1);
  } catch {
    return "";
  }
};

export default function BabaPerfil() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [baba, setBaba] = useState(null);
  const [displayFotoUrl, setDisplayFotoUrl] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [viewerProfile, setViewerProfile] = useState(null);
  const [viewerSaldo, setViewerSaldo] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [showAllReviews, setShowAllReviews] = useState(false);

  const carregarDados = useCallback(async () => {
    setLoading(true);

    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUser(user);

    if (user) {
      const { data: profileData } = await supabase
        .from("usuarios")
        .select(`*, usuarios_clientes(*)`)
        .eq("id", user.id)
        .single();

      if (profileData) {
        const cliente = Array.isArray(profileData.usuarios_clientes)
          ? profileData.usuarios_clientes[0]
          : profileData.usuarios_clientes || {};
        setViewerProfile({ ...profileData, ...cliente });
      }

      const { data: saldoData } = await supabase
        .from("saldo_horas")
        .select("horas_restantes")
        .eq("user_id", user.id);

      if (saldoData) {
        const total = saldoData.reduce((acc, curr) => acc + (Number(curr.horas_restantes) || 0), 0);
        setViewerSaldo(total);
      }
    }

    const { data: perfil } = await supabase
      .from("vw_perfil_baba_completo")
      .select("id, nome, sobrenome, foto_url, cidade, nivel_experiencia, taxa_hora, experiencia_text, bio, area_atuacao, disponibilidade, idiomas_falados, servicos_extras, aceita_emergencial, raio_atendimento_km, avaliacao_media, total_avaliacoes, certificados_docs, data_nascimento")
      .eq("id", id)
      .single();

    if (!perfil) { setBaba(null); setLoading(false); return; }

    setBaba(sanitizePublicBabaProfile(perfil));

    const { data: agendamentos } = await supabase
      .from("agendamentos")
      .select("*")
      .eq("baba_id", id)
      .neq("status", "cancelado");

    setEventos((agendamentos || []).map((a) => ({
      id: a.id,
      title: "Indisponível",
      start: new Date(a.inicio),
      end: new Date(a.fim),
      status: a.status,
    })));

    const { data: reviews } = await supabase
      .from("avaliacoes")
      .select("id, nota, comentario, criado_em")
      .eq("baba_id", id)
      .order("criado_em", { ascending: false });
    setAvaliacoes(reviews || []);

    setLoading(false);
  }, [id]);

  useEffect(() => {
    if (baba) {
      const candidates = getBabaPhotoCandidates(baba.foto_url);
      setDisplayFotoUrl(candidates[0]);
    }
  }, [baba]);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  if (loading) return (
    <div className="flex items-center justify-center py-20 text-sm text-[var(--color-warm-500)]">
      <Clock size={16} className="animate-spin mr-2" /> Carregando...
    </div>
  );
  if (!baba) return (
    <div className="flex items-center justify-center py-20 text-sm text-[var(--color-warm-500)]">
      Perfil não encontrado.
    </div>
  );

  const isOwner = currentUser?.id === baba.id;
  // Só a equipe (admin) mantém contato direto (chat/agenda). Família e visitante
  // passam pelo fluxo intermediado da Tesourinho's (BotaoContratacao).
  const isAdmin = Boolean(viewerProfile?.is_admin) || viewerProfile?.tipo === "admin";
  const isEquipe = isAdmin && !isOwner;

  const campos = ["nome", "sobrenome", "bio", "foto_url", "cidade", "experiencia_text"];
  const preenchidos = campos.filter((c) => baba[c] && String(baba[c]).trim().length > 0).length;
  const porcentagem = Math.round((preenchidos / campos.length) * 100);

  return (
    <>
      <main id="baba-perfil-container" className="max-w-6xl mx-auto px-4 sm:px-5 w-full pb-16">
        {/* TOPO */}
        <section
          id="baba-perfil-topo"
          className="flex flex-col sm:flex-row items-center gap-6 bg-white shadow-sm border border-[var(--color-warm-200)] rounded-2xl mt-28 p-5"
        >
          <img
            id="baba-foto-topo"
            src={displayFotoUrl || PLACEHOLDER_BABA_URL}
            alt={baba.nome}
            className="w-44 h-44 object-cover rounded-xl shrink-0"
            onError={(e) => handleBabaPhotoError(e, getBabaPhotoCandidates(baba.foto_url))}
          />
          <div className="flex-1 min-w-0">
            <h1 id="baba-nome-header" className="text-2xl font-bold text-[var(--color-warm-900)] mb-1">
              {baba.nome} {baba.sobrenome}
            </h1>
            <p id="baba-meta-info" className="text-sm text-[var(--color-warm-500)] mb-4">
              ⭐ {baba.avgRating?.toFixed(1) || "0.0"} ({baba.countReviews || 0} avaliações) · {baba.cidade}
              {baba.nivel_experiencia && ` · ${baba.nivel_experiencia}`}
              {baba.idade && ` · ${baba.idade} anos`}
            </p>
            <div className="flex flex-wrap gap-6 mb-4 px-4 py-3 bg-[var(--color-warm-50)] rounded-xl text-sm">
              <p><strong className="text-[var(--color-warm-700)]">Raio de atendimento:</strong> {baba.raio_atendimento_km}km</p>
              <p><strong className="text-[var(--color-warm-700)]">Aceita emergencial:</strong> {baba.aceita_emergencial ? "✅ Sim" : "❌ Não"}</p>
            </div>
            {isOwner ? null : isEquipe ? (
              <button
                id="baba-btn-chat"
                className="rounded-xl bg-[var(--color-rose-400)] px-5 py-2.5 text-sm font-semibold text-white hover:brightness-105 transition"
                onClick={() => setShowChat(true)}
              >
                Converse Comigo
              </button>
            ) : (
              <div className="max-w-xs">
                <BotaoContratacao baba={baba} />
              </div>
            )}
          </div>
          <section id="baba-secao-disponibilidade" className="shrink-0">
            <CalendarioDisponibilidade disponibilidade={baba.disponibilidade} />
          </section>
        </section>

        {/* BARRA DE PROGRESSO (APENAS PARA A BABÁ) */}
        {isOwner && (
          <div id="baba-progresso-container" className="my-5">
            <div id="baba-barra-progresso" className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                id="baba-progresso-interno"
                className="h-full rounded-full bg-[var(--color-slate-blue-400)] transition-[width] duration-400"
                style={{ width: `${porcentagem}%` }}
              />
            </div>
            <span id="baba-texto-progresso" className="block text-xs text-[var(--color-warm-400)] mt-1.5">
              Seu perfil está {porcentagem}% completo
            </span>
          </div>
        )}

        {/* GRID */}
        <div id="baba-perfil-grid" className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6 mt-6 w-full">
          {/* COLUNA ESQUERDA */}
          <div id="baba-coluna-conteudo">
            <section id="baba-secao-sobre" className={sectionCls}>
              <h2 id="baba-titulo-sobre" className={h2Cls}>Sobre</h2>
              <p className="text-sm text-[var(--color-warm-700)] leading-relaxed">{baba.bio || "Sem descrição."}</p>
            </section>

            <section id="baba-secao-experiencia" className={sectionCls}>
              <h2 id="baba-titulo-experiencia" className={h2Cls}>Experiência</h2>
              <p className="text-sm text-[var(--color-warm-700)] leading-relaxed">{baba.experiencia_text || "Informação não fornecida."}</p>
            </section>

            <section id="baba-secao-atuacao" className={sectionCls}>
              <h2 id="baba-titulo-atuacao" className={h2Cls}>Áreas de Atuação</h2>
              {baba.area_atuacao?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {baba.area_atuacao.map((item) => <span key={item} className={tagCls}>{item}</span>)}
                </div>
              ) : <p className="text-sm text-[var(--color-warm-400)]">Não informado.</p>}
            </section>

            <section id="baba-secao-qualificacoes" className={sectionCls}>
              <h2 id="baba-titulo-qualificacoes" className={h2Cls}>Qualificações</h2>
              <p className="text-sm text-[var(--color-warm-700)] leading-relaxed">{baba.qualificacoes_text || "Certificados e cursos pendentes."}</p>
            </section>

            <section id="baba-secao-certificados" className={sectionCls}>
              <h2 id="baba-titulo-certificados" className={h2Cls}>Certificados</h2>
              {baba.certificados_docs?.length > 0 ? (
                <ul className="space-y-1">
                  {baba.certificados_docs.map((c, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-[var(--color-warm-700)]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-rose-400)] shrink-0" />
                      {c.nome}
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-[var(--color-warm-400)]">Nenhum certificado cadastrado.</p>}
            </section>

            <section id="baba-secao-idiomas" className={sectionCls}>
              <h2 id="baba-titulo-idiomas" className={h2Cls}>Idiomas</h2>
              {baba.idiomas_falados?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {baba.idiomas_falados.map((item) => <span key={item} className={tagCls}>{item}</span>)}
                </div>
              ) : <p className="text-sm text-[var(--color-warm-400)]">Não informado.</p>}
            </section>

            <section id="baba-secao-servicos" className={sectionCls}>
              <h2 id="baba-titulo-servicos" className={h2Cls}>O que eu ofereço</h2>
              {baba.servicos_extras?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {baba.servicos_extras.map((item) => <span key={item} className={tagCls}>{item}</span>)}
                </div>
              ) : <p className="text-sm text-[var(--color-warm-400)]">Não informado.</p>}
            </section>

            <section id="baba-secao-avaliacoes" className={sectionCls}>
              <div className="flex items-center justify-between mb-3">
                <h2 id="baba-titulo-avaliacoes" className={h2Cls}>Avaliações</h2>
                {avaliacoes.length > 0 && (
                  <span className="flex items-center gap-1.5 text-sm">
                    <StarRow nota={baba.avgRating} />
                    <span className="font-semibold text-[var(--color-warm-800)]">
                      {baba.avgRating?.toFixed(1)}
                    </span>
                    <span className="text-[var(--color-warm-400)]">({avaliacoes.length})</span>
                  </span>
                )}
              </div>

              {avaliacoes.length === 0 ? (
                <p className="text-sm text-[var(--color-warm-400)]">
                  Ainda não há avaliações para esta babá.
                </p>
              ) : (
                <div className="space-y-4">
                  {(showAllReviews ? avaliacoes : avaliacoes.slice(0, 5)).map((av) => (
                    <div key={av.id} className="border-b border-[var(--color-warm-100)] last:border-0 pb-4 last:pb-0">
                      <div className="flex items-center justify-between mb-1">
                        <StarRow nota={av.nota} size={13} />
                        <span className="text-xs text-[var(--color-warm-400)]">{formatMesAno(av.criado_em)}</span>
                      </div>
                      {av.comentario && (
                        <p className="text-sm text-[var(--color-warm-700)] leading-relaxed mb-1">{av.comentario}</p>
                      )}
                      <p className="text-xs font-medium text-[var(--color-warm-400)]">Família Tesourinho's</p>
                    </div>
                  ))}

                  {avaliacoes.length > 5 && !showAllReviews && (
                    <button
                      onClick={() => setShowAllReviews(true)}
                      className="text-sm font-semibold text-[var(--color-rose-400)] hover:underline"
                    >
                      Ver mais avaliações
                    </button>
                  )}
                </div>
              )}
            </section>
          </div>

          {/* COLUNA DIREITA */}
          <aside id="baba-card-reserva" className="lg:sticky lg:top-24 h-fit">
            {isOwner ? (
              <div id="baba-status-owner" className="bg-white rounded-2xl border border-[var(--color-warm-100)] shadow-sm p-5">
                <h2 id="baba-titulo-status" className={h2Cls}>Status da Conta</h2>
                <button
                  id="baba-btn-editar"
                  className="w-full rounded-xl bg-[var(--color-rose-400)] py-2.5 text-sm font-semibold text-white hover:brightness-105 transition mb-4"
                  onClick={() => navigate("/dashboard-baba")}
                >
                  Editar perfil
                </button>
                <ul id="baba-lista-verificacao" className="space-y-2 text-sm text-[var(--color-warm-500)]">
                  <li id="status-item-busca">✔ Perfil visível na busca</li>
                  <li id="status-item-analise">✔ Documentação em análise</li>
                  <li id="status-item-referencias">✔ Verificação de referências</li>
                </ul>
              </div>
            ) : isEquipe ? (
              <div id="baba-area-reserva" className="bg-white rounded-2xl border border-[var(--color-warm-100)] shadow-sm p-5">
                <h2 id="baba-titulo-agenda" className={h2Cls}>Calendário de Agendamento</h2>
                <div id="baba-calendario-wrapper" className="my-4 h-96">
                  <CalendarioAgenda
                    eventos={eventos}
                    disponibilidade={baba.disponibilidade}
                    user={currentUser}
                    babaId={baba.id}
                    saldoHoras={viewerSaldo}
                    nomeBaba={baba.nome}
                    fetchEventos={carregarDados}
                  />
                </div>
              </div>
            ) : (
              <div id="baba-area-contratacao" className="bg-white rounded-2xl border border-[var(--color-warm-100)] shadow-sm p-5">
                <h2 id="baba-titulo-contratacao" className={h2Cls}>Contratar com segurança</h2>
                <div className="flex items-start gap-2.5 mb-4 rounded-xl bg-[var(--color-warm-50)] px-3 py-3">
                  <ShieldCheck size={18} className="text-[var(--color-slate-blue-400)] shrink-0 mt-0.5" />
                  <p className="text-xs text-[var(--color-warm-600)] leading-relaxed">
                    A Tesourinho's intermedia todo o contato. Você solicita a
                    contratação e nossa equipe cuida do resto — com total segurança
                    e privacidade.
                  </p>
                </div>
                <BotaoContratacao baba={baba} />
              </div>
            )}
          </aside>
        </div>
      </main>

      {showChat && currentUser && (
        <ChatWhatsapp
          senderId={currentUser.id}
          destinatarioId={baba.id}
          nomeDestinatario={baba.nome}
          onClose={() => setShowChat(false)}
        />
      )}
    </>
  );
}
